import React, { useEffect, useRef, useState } from 'react';

/**
 * TradingView's free embeddable widgets (https://www.tradingview.com/widget-docs/).
 *
 * Each widget is a script from TradingView's CDN that reads its JSON settings
 * from its own body and draws an iframe next to itself. We rebuild it when the
 * settings or the app's light/dark theme change. TradingView's terms ask for
 * their attribution link to stay visible, so every embed shows one.
 *
 * Market data only: nothing here touches anyone's TradingView account.
 */

const SCRIPT_BASE = 'https://s3.tradingview.com/external-embedding/embed-widget-';

/** 'light' | 'dark', following the app's theme (body.light-mode). */
export function useAppTheme() {
  const read = () => (typeof document !== 'undefined' && document.body.classList.contains('light-mode') ? 'light' : 'dark');
  const [theme, setTheme] = useState(read);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    const obs = new MutationObserver(() => setTheme(read()));
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

/** Symbols typed by a user: "NASDAQ:AAPL, bitstamp:btcusd" → ['NASDAQ:AAPL', 'BITSTAMP:BTCUSD']. */
export function parseSymbols(text, max = 20) {
  return String(text || '')
    .split(/[\s,;]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => /^[A-Z0-9_]+:[A-Z0-9_.!\-/]+$/.test(s) || /^[A-Z0-9.!-]+$/.test(s))
    .slice(0, max);
}

/*
 * TradingView's script finds its container through its own parent element. If
 * the container is removed while the script is still downloading (React's
 * development double-mount, a theme switch, a widget removed right away), the
 * script runs with no parent and throws; the browser reports it only as
 * "Script error." because it comes from another site. So: wait a tick before
 * inserting (a double-mount cancels cleanly), and if we must tear down while
 * the script is still loading, park its box off-screen until it has run.
 */
let parking = null;
function parkingLot() {
  if (!parking) {
    parking = document.createElement('div');
    parking.setAttribute('aria-hidden', 'true');
    parking.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;overflow:hidden;visibility:hidden;pointer-events:none;';
    document.body.appendChild(parking);
  }
  return parking;
}

/**
 * A TradingView frame that grabs focus while loading (its error dialog does,
 * e.g. for a symbol free embeds don't allow) makes the browser scroll the page
 * to it, so opening a dashboard jumped to the bottom. For a few seconds after an
 * embed starts, if the page scrolls with nobody touching it and focus is inside
 * one of these frames, put every scroll position back.
 */
const GUARD_MS = 6000;
function guardScrollWhileLoading(host) {
  if (typeof window === 'undefined') return () => {};
  const scrollers = [];
  for (let el = host.parentElement; el; el = el.parentElement) {
    if (el.scrollHeight > el.clientHeight + 1) scrollers.push([el, el.scrollTop]);
  }
  const winY = window.scrollY;
  let userActed = false;
  const acted = () => { userActed = true; };
  const restore = () => {
    if (userActed) return;
    const active = document.activeElement;
    if (!active || active.tagName !== 'IFRAME' || !active.closest('.tv-embed-host')) return;
    scrollers.forEach(([el, top]) => { if (el.scrollTop !== top) el.scrollTop = top; });
    if (window.scrollY !== winY) window.scrollTo(window.scrollX, winY);
  };
  const userEvents = ['pointerdown', 'wheel', 'keydown', 'touchstart'];
  userEvents.forEach((e) => window.addEventListener(e, acted, { capture: true, passive: true }));
  window.addEventListener('scroll', restore, { capture: true, passive: true });
  const onBlur = () => setTimeout(restore, 0);
  window.addEventListener('blur', onBlur);
  const stop = () => {
    userEvents.forEach((e) => window.removeEventListener(e, acted, { capture: true }));
    window.removeEventListener('scroll', restore, { capture: true });
    window.removeEventListener('blur', onBlur);
  };
  const t = setTimeout(stop, GUARD_MS);
  return () => { clearTimeout(t); stop(); };
}

export default function TradingViewEmbed({ widget, settings, className = '', label = 'Market data by TradingView' }) {
  const hostRef = useRef(null);
  const theme = useAppTheme();
  const settingsKey = JSON.stringify(settings || {});

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let box = null;
    let script = null;
    let done = false;
    const unguard = guardScrollWhileLoading(host);
    const timer = setTimeout(() => {
      box = document.createElement('div');
      // TradingView places its frame inside __widget only when the script's
      // parent is the .tradingview-widget-container.
      box.className = 'tradingview-widget-container tv-embed-box';
      const inner = document.createElement('div');
      inner.className = 'tradingview-widget-container__widget';
      box.appendChild(inner);
      script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `${SCRIPT_BASE}${widget}.js`;
      const finish = () => { done = true; };
      script.addEventListener('load', finish);
      script.addEventListener('error', finish);
      // The advanced chart reads `theme`; the others read `colorTheme`.
      script.innerHTML = JSON.stringify({
        locale: 'en',
        isTransparent: true,
        ...JSON.parse(settingsKey),
        colorTheme: theme,
        theme,
        // The advanced chart ignores isTransparent: blend it into the card instead.
        ...(widget === 'advanced-chart' ? {
          backgroundColor: theme === 'light' ? '#ffffff' : '#0e1429',
          gridColor: theme === 'light' ? 'rgba(15, 23, 42, 0.06)' : 'rgba(148, 163, 184, 0.08)',
        } : {}),
      });
      box.appendChild(script);
      host.appendChild(box);
    }, 0);

    return () => {
      clearTimeout(timer);
      unguard();
      if (!box) return;
      if (done) { box.remove(); return; }
      parkingLot().appendChild(box);
      const drop = () => setTimeout(() => box.remove(), 2000);
      script.addEventListener('load', drop);
      script.addEventListener('error', drop);
    };
  }, [widget, settingsKey, theme]);

  return (
    <div className={`tv-embed ${className}`}>
      <div className="tv-embed-host" ref={hostRef} />
      <a className="tv-embed-attrib" href="https://www.tradingview.com/" rel="noopener nofollow noreferrer" target="_blank">
        {label}
      </a>
    </div>
  );
}
